import React, { useEffect, useRef } from "react";

const ConfettiCanvas = ({ anchorSelector = "#payment-success-block" }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    // Устанавливаем размер canvas
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    // Математические константы
    const TWO_PI = Math.PI * 2;
    const HALF_PI = Math.PI * 0.5;
    const timeStep = 1 / 60;

    // Получаем координаты anchor элемента
    let startX = window.innerWidth * 0.5;
    let startY = window.innerHeight * 0.5;

    if (anchorSelector) {
      const anchor = document.querySelector(anchorSelector);
      if (anchor) {
        const rect = anchor.getBoundingClientRect();
        startX = rect.left + rect.width * 0.5;
        startY = rect.top + rect.height * 0.5;
      }
    }

    // Класс точки
    function Point(x, y) {
      this.x = x || 0;
      this.y = y || 0;
    }

    // Класс частицы конфетти
    function Particle(p0, p1, p2, p3) {
      this.p0 = p0;
      this.p1 = p1;
      this.p2 = p2;
      this.p3 = p3;

      this.time = 0;
      this.duration = 3 + Math.random() * 2;
      this.color = [
        "#42c2f1",
        "#fbda4f",
        "#ab63df",
        "#5fc581",
        "#ff6b6b",
        "#4ecdc4",
        "#45b7d1",
        "#f9ca24",
      ][Math.floor(Math.random() * 8)];

      this.w = 8;
      this.h = 6;

      this.complete = false;
      this.x = p0.x;
      this.y = p0.y;
      this.r = 0;
      this.sy = 1;
    }

    Particle.prototype = {
      update: function () {
        this.time = Math.min(this.duration, this.time + timeStep);

        const f = this.easeOutCubic(this.time, 0, 1, this.duration);
        const p = this.cubeBezier(this.p0, this.p1, this.p2, this.p3, f);

        const dx = p.x - this.x;
        const dy = p.y - this.y;

        this.r = Math.atan2(dy, dx) + HALF_PI;
        this.sy = Math.sin(Math.PI * f * 10);
        this.x = p.x;
        this.y = p.y;

        this.complete = this.time === this.duration;
      },

      draw: function () {
        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.rotate(this.r);
        ctx.scale(1, this.sy);

        ctx.fillStyle = this.color;
        ctx.fillRect(-this.w * 0.5, -this.h * 0.5, this.w, this.h);

        ctx.restore();
      },

      easeOutCubic: function (t, b, c, d) {
        t /= d;
        t--;
        return c * (t * t * t + 1) + b;
      },

      cubeBezier: function (p0, c0, c1, p1, t) {
        const p = new Point();
        const nt = 1 - t;

        p.x =
          nt * nt * nt * p0.x +
          3 * nt * nt * t * c0.x +
          3 * nt * t * t * c1.x +
          t * t * t * p1.x;
        p.y =
          nt * nt * nt * p0.y +
          3 * nt * nt * t * c0.y +
          3 * nt * t * t * c1.y +
          t * t * t * p1.y;

        return p;
      },
    };

    // Создаем частицы
    const particles = [];
    for (let i = 0; i < 120; i++) {
      const p0 = new Point(startX, startY);
      const p1 = new Point(
        startX + (Math.random() - 0.5) * 300,
        startY + (Math.random() - 0.5) * 200
      );
      const p2 = new Point(
        Math.random() * canvas.width,
        Math.random() * canvas.height * 0.7
      );
      const p3 = new Point(Math.random() * canvas.width, canvas.height + 100);

      particles.push(new Particle(p0, p1, p2, p3));
    }

    // Функция анимации
    function animate() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      let allComplete = true;
      particles.forEach((particle) => {
        particle.update();
        particle.draw();
        if (!particle.complete) allComplete = false;
      });

      if (!allComplete) {
        animationRef.current = requestAnimationFrame(animate);
      }
    }

    // Запускаем анимацию
    animate();

    // Cleanup функция
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [anchorSelector]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        pointerEvents: "none",
        zIndex: 99999,
      }}
    />
  );
};

export default ConfettiCanvas;
